import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Menu,
  Avatar,
  Dropdown,
  Tooltip,
  Badge,
  Modal
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  HeartFilled
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faSliders, faFileImport, faPhone, faUsers, faTerminal } from '@fortawesome/free-solid-svg-icons';
import {
  Home,
  Calendar,
  CalendarDays,
  CalendarCheck,
  Clock,
  Users,
  UsersRound,
  Megaphone,
  Heart,
  Send,
  Inbox,
  ClipboardList,
  Mail,
  FileText,
  Zap,
  PieChart,
  Settings,
  HeartHandshake,
  ScrollText,
  Phone,
  BriefcaseMedical,
  Warehouse,
  Stethoscope,
  Wallet,
  Package,
  ShieldCheck
} from 'lucide-react';
import { useStore } from '../../hooks/storeContext';
import { ROUTES } from '../../constants/routes';
import { COLORS } from '../../constants/colors';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import PatientService from '../../services/PatientService';
import { unsubscribeFromPush } from '../../services/webPush';
import NotificationBell from '../../components/NotificationBell';
import DialerModal from '../../components/DialerModal';
import ActiveCallBar from '../../components/ActiveCallBar';
import IncomingCallBanner from '../../components/IncomingCallBanner';
import TopNavBar from './TopNavBar';
import MobileBottomNav from './MobileBottomNav';
import { PageHeaderProvider, usePageHeader } from '../../contexts/PageHeaderContext';
import { ActiveCallProvider, useActiveCall } from '../../contexts/ActiveCallContext';
import { SearchProvider } from '../../contexts/SearchContext';
import CommandPalette from '../../components/CommandPalette';
import {
  StyledLayout,
  StyledSider,
  Logo,
  ControlButtonsWrapper,
  CollapseButton,
  StyledContent,
  ContentScrollArea,
  MobileWrapper,
  ProfileButton,
  ProfilePopover,
  MobileTopBar,
  MobileTopBarTitle,
  MobileTopBarActions,
  menuStyles,
  avatarStyles
} from './styled';

// Shared props for sidebar menu icons (outline style via lucide-react).
// strokeWidth tuned to feel close to Ant Design's default outline weight.
// When the menu item is selected, the icon is "filled" via CSS in styled.js
// (fill: currentColor on the SVG paths).
const NAV_ICON_PROPS = { size: 20, strokeWidth: 1.75 };

const AdminLayout = observer(({ children }) => {
  // Initialize collapsed state from localStorage or default to false
  const [collapsed, setCollapsed] = useState(() => {
    const savedCollapsed = localStorage.getItem('adminSidebarCollapsed');
    return savedCollapsed ? JSON.parse(savedCollapsed) : false;
  });
  const [profilePopupVisible, setProfilePopupVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [intakeCount, setIntakeCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { authStore } = useStore();
  const { canInstall, showInstall, isIOS, promptInstall } = useInstallPrompt();

  // Trigger the native install prompt when available; otherwise (e.g. iOS
  // Safari, which never fires `beforeinstallprompt`) show manual instructions.
  const handleInstall = () => {
    if (canInstall) {
      promptInstall();
      return;
    }
    Modal.info({
      title: 'Install App',
      content: isIOS
        ? 'Tap the Share button in Safari, then choose "Add to Home Screen".'
        : 'Open your browser menu, then choose "Install app" or "Add to Home screen".',
      okText: 'Got it'
    });
  };
  
  // Active call context
  const { activeCall, isCallActive, initiateCall, endCall, toggleMute, incomingCall, acceptIncoming, dismissIncoming } = useActiveCall();

  // Auto-open submenus if on a submenu page
  useEffect(() => {
    const keysToOpen = [];
    if (
      location.pathname.startsWith('/admin/analytics') ||
      location.pathname.startsWith('/admin/call-logs') ||
      location.pathname.startsWith('/admin/insurance-enrollments')
    ) {
      keysToOpen.push('analytics');
    }
    if (location.pathname.startsWith('/admin/ondemand')) {
      keysToOpen.push('ondemand');
    }
    if (
      location.pathname.startsWith('/admin/case-management') ||
      location.pathname.startsWith('/admin/campaigns') ||
      location.pathname.startsWith('/admin/email-templates') ||
      location.pathname.startsWith('/admin/email-automations') ||
      location.pathname.startsWith('/admin/surveys')
    ) {
      keysToOpen.push('communications');
    }
    if (keysToOpen.length > 0) {
      setOpenKeys(keysToOpen);
    }
  }, [location.pathname]);

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Save collapsed state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('adminSidebarCollapsed', JSON.stringify(collapsed));
    
    // Add/remove body class to indicate sidebar state for modal positioning
    if (collapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('sidebar-collapsed');
    };
  }, [collapsed]);

  // Poll the number of people currently in the intake queue so the
  // sidebar badge stays accurate even when not on the Intake page.
  useEffect(() => {
    const canViewIntake = authStore.isAdmin || authStore.hasPermission('patient_queue');
    if (!canViewIntake) {
      setIntakeCount(0);
      return;
    }

    let cancelled = false;
    const fetchIntakeCount = async () => {
      try {
        const response = await PatientService.getQueueCounts();
        if (!cancelled && response?.success && response.data) {
          setIntakeCount(response.data.active || 0);
        }
      } catch (error) {
        // Non-critical: leave the previous count in place on failure
        console.error('Error fetching intake count for sidebar badge:', error);
      }
    };

    fetchIntakeCount();
    const interval = setInterval(fetchIntakeCount, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [authStore.isAdmin, authStore]);

  const handleLogout = async () => {
    try {
      // Fire-and-forget: push cleanup must never block or delay logout.
      unsubscribeFromPush().catch(() => {});
      await authStore.logout();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleProfileMenuClick = (action) => {
    setProfilePopupVisible(false);
    
    switch (action) {
      case 'profile':
        navigate(ROUTES.PROFILE);
        break;
      case 'settings':
        navigate(ROUTES.SETTINGS);
        break;
      case 'users':
        navigate(ROUTES.ADMIN_USERS);
        break;
      case 'formConfig':
        navigate(ROUTES.ADMIN_FORM_CONFIG);
        break;
      case 'dataImport':
        navigate(ROUTES.ADMIN_DATA_IMPORT);
        break;
      case 'backendLogs':
        navigate(ROUTES.ADMIN_BACKEND_LOGS);
        break;
      case 'logout':
        handleLogout();
        break;
      default:
        break;
    }
  };

  // Define all possible menu items with their required permissions
  // Compute selectedKeys: map child routes to their parent menu item
  const getSelectedKeys = () => {
    const path = location.pathname;
    // Member record edit pages → highlight Members nav item
    if (path.startsWith('/admin/member-records/') || path.startsWith('/admin/screening-entry/')) {
      return [ROUTES.ADMIN_ALL_MEMBERS];
    }
    // Patient edit from queue → highlight Intake nav item
    if (path.startsWith('/admin/patient-edit/')) {
      return [ROUTES.ADMIN_PATIENT_QUEUE];
    }
    // Event detail pages → highlight Bookings nav item
    if (path.match(/^\/admin\/events\/.+/)) {
      return [ROUTES.ADMIN_EVENTS];
    }
    // Survey builder / responses / preview → highlight Surveys nav item
    if (path.startsWith('/admin/surveys/')) {
      return [ROUTES.ADMIN_SURVEYS];
    }
    // Email campaign builder / analytics → highlight Campaigns nav item
    if (path.startsWith('/admin/campaigns/')) {
      return [ROUTES.ADMIN_EMAIL_CAMPAIGNS];
    }
    // Email automation builder → highlight Automations nav item
    if (path.startsWith('/admin/email-automations/')) {
      return [ROUTES.ADMIN_EMAIL_AUTOMATIONS];
    }
    return [path];
  };
  const selectedKeys = getSelectedKeys();
  const allMenuItems = [
    {
      key: ROUTES.ADMIN_DASHBOARD,
      icon: <Home {...NAV_ICON_PROPS} />,
      label: 'Dashboard',
      permission: 'dashboard'
    },
    {
      key: ROUTES.ADMIN_EVENTS,
      icon: <Calendar {...NAV_ICON_PROPS} />,
      label: 'Bookings',
      permission: 'event_management'
    },
    {
      key: ROUTES.ADMIN_PATIENT_QUEUE,
      icon: collapsed ? (
        <Badge
          count={intakeCount}
          size="small"
          offset={[2, -2]}
          overflowCount={99}
          style={{ backgroundColor: COLORS.$primaryBlue, color: COLORS.$white, boxShadow: 'none' }}
        >
          <Clock {...NAV_ICON_PROPS} />
        </Badge>
      ) : (
        <Clock {...NAV_ICON_PROPS} />
      ),
      label: (
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingRight: 4 }}>
          <span>Intake</span>
          {intakeCount > 0 && (
            <Badge
              count={intakeCount}
              size="small"
              overflowCount={99}
              style={{
                backgroundColor: COLORS.$primaryBlue,
                color: COLORS.$white,
                padding: '0 8px',
                height: 20,
                lineHeight: '20px',
                borderRadius: 10,
                boxShadow: 'none'
              }}
            />
          )}
        </span>
      ),
      title: 'Intake',
      permission: 'patient_queue'
    },
    {
      key: ROUTES.ADMIN_ALL_MEMBERS,
      icon: <Users {...NAV_ICON_PROPS} />,
      label: 'Members',
      permission: 'screening_data'
    },
    {
      key: ROUTES.ADMIN_PULSE,
      icon: <Heart {...NAV_ICON_PROPS} />,
      label: 'Pulse',
      permission: 'analytics'
    },
    // Form Config, Data Import, and Users moved to the profile popover
    // (above Logout, below Settings) to keep the main sidebar focused on
    // day-to-day workflows.
    {
      key: ROUTES.ADMIN_INBOX,
      icon: <Inbox {...NAV_ICON_PROPS} />,
      label: 'Inbox',
      permission: 'messaging'
    },
    {
      key: 'communications',
      icon: <Send {...NAV_ICON_PROPS} />,
      label: 'Communications',
      children: [
        {
          key: ROUTES.ADMIN_SURVEYS,
          icon: <ClipboardList {...NAV_ICON_PROPS} />,
          label: 'Surveys',
          adminOnly: true
        },
        {
          key: ROUTES.ADMIN_CASE_MANAGEMENT,
          icon: <Megaphone {...NAV_ICON_PROPS} />,
          label: 'Outreach',
          permission: 'case_management'
        },
        {
          key: ROUTES.ADMIN_EMAIL_CAMPAIGNS,
          icon: <Mail {...NAV_ICON_PROPS} />,
          label: 'Email Campaigns',
          adminOnly: true
        },
        {
          key: ROUTES.ADMIN_EMAIL_TEMPLATES,
          icon: <FileText {...NAV_ICON_PROPS} />,
          label: 'Templates',
          adminOnly: true
        },
        {
          key: ROUTES.ADMIN_EMAIL_AUTOMATIONS,
          icon: <Zap {...NAV_ICON_PROPS} />,
          label: 'Automations',
          adminOnly: true
        }
      ]
    },
    {
      key: 'analytics',
      icon: <PieChart {...NAV_ICON_PROPS} />,
      label: 'Analytics',
      children: [
        {
          key: ROUTES.ADMIN_ANALYTICS_REPORTS,
          icon: <FileText {...NAV_ICON_PROPS} />,
          label: 'Reports',
          permission: 'analytics'
        },
        {
          key: ROUTES.ADMIN_ANALYTICS_HEALTH_OUTCOMES,
          icon: <Heart {...NAV_ICON_PROPS} />,
          label: 'Health Outcomes',
          permission: 'analytics'
        },
        {
          key: ROUTES.ADMIN_INSURANCE_ENROLLMENTS,
          icon: <ShieldCheck {...NAV_ICON_PROPS} />,
          label: 'Insurance',
          permission: 'insurance_enrollment'
        },
        {
          key: ROUTES.ADMIN_ANALYTICS_OPERATIONS,
          icon: <Settings {...NAV_ICON_PROPS} />,
          label: 'Operations',
          permission: 'analytics'
        },
        // {
        //   key: ROUTES.ADMIN_ANALYTICS_WAIT_TIMES,
        //   icon: <Timer {...NAV_ICON_PROPS} />,
        //   label: 'Wait Times'
        // },
        {
          key: ROUTES.ADMIN_ANALYTICS_EVENTS,
          icon: <CalendarCheck {...NAV_ICON_PROPS} />,
          label: 'Events',
          permission: 'analytics'
        },
        {
          key: ROUTES.ADMIN_ANALYTICS_BOOKING,
          icon: <CalendarDays {...NAV_ICON_PROPS} />,
          label: 'Booking',
          permission: 'analytics'
        },
        {
          key: ROUTES.ADMIN_ANALYTICS_POPULATION,
          icon: <UsersRound {...NAV_ICON_PROPS} />,
          label: 'Population',
          permission: 'analytics'
        },
        {
          key: ROUTES.ADMIN_ANALYTICS_VOLUNTEERS,
          icon: <HeartHandshake {...NAV_ICON_PROPS} />,
          label: 'Volunteers',
          permission: 'analytics'
        },
        {
          key: ROUTES.ADMIN_AUDIT_LOGS,
          icon: <ScrollText {...NAV_ICON_PROPS} />,
          label: 'Logs',
          permission: 'audit_logs'
        },
        {
          type: 'divider'
        },
        {
          key: ROUTES.ADMIN_CALL_LOGS,
          icon: <Phone {...NAV_ICON_PROPS} />,
          label: 'Call Logs',
          permission: 'voice_calls'
        }
      ]
    },
    {
      type: 'divider'
    },
    {
      key: 'ondemand',
      icon: <BriefcaseMedical {...NAV_ICON_PROPS} />,
      label: 'Deployed Hearts',
      permission: 'ondemand_management',
      children: [
        {
          key: ROUTES.ADMIN_ONDEMAND_STORAGE,
          icon: <Warehouse {...NAV_ICON_PROPS} />,
          label: 'Storage Units',
          permission: 'ondemand_management'
        },
        {
          key: ROUTES.ADMIN_ONDEMAND_HCWS,
          icon: <Stethoscope {...NAV_ICON_PROPS} />,
          label: 'Healthcare Workers',
          permission: 'ondemand_management'
        },
        {
          key: ROUTES.ADMIN_ONDEMAND_EVENTS,
          icon: <CalendarCheck {...NAV_ICON_PROPS} />,
          label: 'Events',
          permission: 'ondemand_management'
        },
        {
          key: ROUTES.ADMIN_ONDEMAND_PATIENTS,
          icon: <Users {...NAV_ICON_PROPS} />,
          label: 'Patients',
          permission: 'ondemand_management'
        },
        {
          key: ROUTES.ADMIN_ONDEMAND_PAYOUTS,
          icon: <Wallet {...NAV_ICON_PROPS} />,
          label: 'Payouts',
          permission: 'ondemand_management'
        },
        {
          key: ROUTES.ADMIN_ONDEMAND_ORDERS,
          icon: <Package {...NAV_ICON_PROPS} />,
          label: 'Orders',
          permission: 'ondemand_management'
        }
      ]
    },
  ];

  // Filter menu items based on user permissions (recursively for children)
  const filterMenuItem = (item) => {
    // Always show dividers
    if (item.type === 'divider') return true;
    // Admins see everything
    if (authStore.isAdmin) return true;
    // Pages behind RequireAdmin (their APIs are admin-only too)
    if (item.adminOnly) return false;
    // If item has its own permission requirement, enforce it
    if (item.permission && !authStore.hasPermission(item.permission)) return false;
    // If it has children, ensure at least one child is visible
    if (item.children) {
      const visibleChildren = item.children.filter(filterMenuItem);
      // Strip leading/trailing dividers from children
      while (visibleChildren.length && visibleChildren[0].type === 'divider') visibleChildren.shift();
      while (visibleChildren.length && visibleChildren[visibleChildren.length - 1].type === 'divider') visibleChildren.pop();
      if (visibleChildren.length === 0) return false;
      item.children = visibleChildren;
    }
    return true;
  };

  const sidebarMenuItems = allMenuItems
    .map(item => item.children ? { ...item, children: [...item.children] } : item)
    .filter(filterMenuItem);

  const handleMenuClick = ({ key }) => {
    // Don't navigate for parent items (they just toggle submenu)
    if (key === 'analytics' || key === 'ondemand' || key === 'communications') return;
    navigate(key);
  };

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  // Get current page title based on route
  const getPageTitle = () => {
    const path = location.pathname;
    // Check in main items first
    let menuItem = allMenuItems.find(item => item.key === path);
    // If not found, check in children (analytics sub-items)
    if (!menuItem) {
      for (const item of allMenuItems) {
        if (item.children) {
          const child = item.children.find(c => c.key === path);
          if (child) {
            menuItem = child;
            break;
          }
        }
      }
    }
    return menuItem?.title || menuItem?.label || 'Dashboard';
  };

  return (
    <MobileWrapper>
      {/* Mobile Top Bar - Only visible on mobile */}
      <MobileTopBar>
        <MobileTopBarTitle>
          {getPageTitle()}
        </MobileTopBarTitle>
        <MobileTopBarActions>
          <Tooltip title="Quick Call">
            <span
              onClick={() => setDialerOpen(true)}
              aria-label="Open quick call dialer"
              style={{
                color: '#1B5E20',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                transition: 'all 0.2s ease'
              }}
            >
              <FontAwesomeIcon icon={faPhone} />
            </span>
          </Tooltip>
          <NotificationBell />
        </MobileTopBarActions>
      </MobileTopBar>

      <StyledLayout>
        {/* Desktop Sidebar */}
        {!isMobile && (
          <StyledSider 
            trigger={null} 
            collapsible 
            collapsed={collapsed}
            width={250}          
            collapsedWidth={80}
          >
            <Logo collapsed={collapsed}>
              {collapsed ? (
                <img src="/large-b-logo.png" alt="BHA Logo" />
              ) : (
                <>
                  <HeartFilled className="brand-heart" />
                  <span className="brand-text">Black Heart</span>
                </>
              )}
            </Logo>
            
            <Menu
              mode="inline"
              selectedKeys={selectedKeys}
              {...(!collapsed && { openKeys, onOpenChange: handleOpenChange })}
              items={sidebarMenuItems}
              onClick={handleMenuClick}
              style={menuStyles}
            />
            
            <ControlButtonsWrapper collapsed={collapsed}>
              <CollapseButton 
                collapsed={collapsed}
                onClick={() => setCollapsed(!collapsed)}
              >
                <div className="collapse-button-inner">
                  <div className="collapse-icon">
                    {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  </div>
                  <div className="collapse-text">
                    {collapsed ? 'Expand' : 'Collapse'}
                  </div>
                </div>
              </CollapseButton>
            </ControlButtonsWrapper>
            
            <Dropdown
              dropdownRender={() => (
                <ProfilePopover>
                  {/* Settings, Form Config, Data Import and Backend Logs are admin-only pages */}
                  {authStore.isAdmin && (
                    <>
                      <button 
                        className="profile-menu-item"
                        onClick={() => handleProfileMenuClick('settings')}
                      >
                        <SettingOutlined />
                        <span>Settings</span>
                      </button>
                      <button
                        className="profile-menu-item"
                        onClick={() => handleProfileMenuClick('formConfig')}
                      >
                        <FontAwesomeIcon icon={faSliders} />
                        <span>Form Config</span>
                      </button>
                      <button
                        className="profile-menu-item"
                        onClick={() => handleProfileMenuClick('dataImport')}
                      >
                        <FontAwesomeIcon icon={faFileImport} />
                        <span>Data Import</span>
                      </button>
                      <button
                        className="profile-menu-item"
                        onClick={() => handleProfileMenuClick('backendLogs')}
                      >
                        <FontAwesomeIcon icon={faTerminal} />
                        <span>Backend Logs</span>
                      </button>
                    </>
                  )}
                  {(authStore.isAdmin || authStore.hasPermission('user_management')) && (
                    <>
                      <button
                        className="profile-menu-item"
                        onClick={() => handleProfileMenuClick('users')}
                      >
                        <FontAwesomeIcon icon={faUsers} />
                        <span>Users</span>
                      </button>
                    </>
                  )}
                  <button 
                    className="profile-menu-item logout-item"
                    onClick={() => handleProfileMenuClick('logout')}
                  >
                    <LogoutOutlined />
                    <span>Logout</span>
                  </button>
                </ProfilePopover>
              )}
              trigger={['click']}
              placement="topLeft"
              open={profilePopupVisible}
              onOpenChange={setProfilePopupVisible}
            >
              <ProfileButton 
                collapsed={collapsed}
                onClick={() => setProfilePopupVisible(!profilePopupVisible)}
              >
                <div className="profile-button-inner">
                  <div className="profile-content">
                    <div className="left-section">
                      <Avatar 
                        style={avatarStyles}
                        icon={<UserOutlined />}
                      />
                      <div className="user-info">
                        <div className="user-name">
                          {authStore.me?.fullName || 'Admin User'}
                        </div>
                        <div className="user-role">{authStore.isAdmin ? 'Administrator' : 'Staff'}</div>
                      </div>
                    </div>
                    <FontAwesomeIcon icon={faChevronUp} className="caret" />
                  </div>
                </div>
              </ProfileButton>
            </Dropdown>
          </StyledSider>
        )}

        <StyledContent collapsed={collapsed}>
          <TopNavBarRenderer 
            onDialerOpen={() => setDialerOpen(true)}
          />
          <ContentScrollArea>
            {children}
          </ContentScrollArea>
        </StyledContent>
      </StyledLayout>

      {/* Mobile bottom navigation - shown via CSS media query (<=1024px) */}
      <MobileBottomNav
        selectedKeys={selectedKeys}
        intakeCount={intakeCount}
        userName={authStore.me?.fullName || 'Admin User'}
        canManageUsers={authStore.isAdmin || authStore.hasPermission('user_management')}
        isAdmin={authStore.isAdmin}
        hasPermission={(permission) => authStore.hasPermission(permission)}
        canInstall={showInstall}
        onInstall={handleInstall}
        onLogout={handleLogout}
      />

      {/* Quick Call Dialer Modal - Global for mobile */}
      <DialerModal
        isOpen={dialerOpen}
        onClose={() => setDialerOpen(false)}
        onCall={(fullNumber, contact, agent, callType, fromNumber) => {
          initiateCall({
            fullNumber,
            contact,
            agent,
            callType,
            fromNumber
          });
        }}
      />
      
      {/* Active Call Bar - Shows at bottom when call is active */}
      <ActiveCallBar
        visible={isCallActive}
        callData={activeCall}
        contact={activeCall?.contact}
        fromNumber={activeCall?.fromNumber}
        toNumber={activeCall?.toNumber}
        callType={activeCall?.callType}
        agentName={activeCall?.agent?.name}
        onMuteToggle={toggleMute}
        onEndCall={endCall}
        onContactClick={() => console.log('Contact click')}
      />

      <IncomingCallBanner
        incomingCall={incomingCall}
        onAccept={acceptIncoming}
        onDismiss={dismissIncoming}
      />
    </MobileWrapper>
  );
});

// Inner component to render TopNavBar using context
const TopNavBarRenderer = ({ onDialerOpen }) => {
  const { headerProps } = usePageHeader();
  
  return (
    <TopNavBar
      title={headerProps.title}
      icon={headerProps.icon}
      count={headerProps.count}
      countIcon={headerProps.countIcon}
      loading={headerProps.loading}
      loadingType={headerProps.loadingType}
      actions={headerProps.actions}
      enableNotificationPolling={headerProps.enableNotificationPolling}
      onDialerOpen={onDialerOpen}
      breadcrumbs={headerProps.breadcrumbs}
      customBreadcrumbLabel={headerProps.customBreadcrumbLabel}
    />
  );
};

// Wrapper component that provides the context
const AdminLayoutWithContext = observer(({ children }) => {
  return (
    <ActiveCallProvider>
      <PageHeaderProvider>
        <SearchProvider>
          <AdminLayout>{children}</AdminLayout>
          <CommandPalette />
        </SearchProvider>
      </PageHeaderProvider>
    </ActiveCallProvider>
  );
});

export default AdminLayoutWithContext;
export { usePageHeader, useActiveCall };
